// Authentication service for handling user authentication and profile management
import { User, UserRole } from '../data/types';
import { mockTeachers } from '../data/mockTeachers';
import { mockStudents } from '../data/mockStudents';
import { dataService } from './dataService';

// Mock current user - in a real app, this would be managed with proper auth
let currentUser: User | null = null;

export const authService = {
  // Initialize with a default user for development
  initialize() {
    // Default to the first teacher for development
    if (!currentUser && mockTeachers.length > 0) {
      currentUser = mockTeachers[0];
    }
  },

  // Get the current logged in user
  getCurrentUser(): User | null {
    return currentUser;
  },

  // Login with username
  async login(username: string, role: UserRole): Promise<User | null> {
    // In a real app, this would validate credentials against a backend
    let user: User | null = null;
    
    if (role === 'Teacher') {
      const teacher = mockTeachers.find(t => t.username === username);
      if (teacher) user = teacher;
    } else if (role === 'Student') {
      const student = mockStudents.find(s => s.username === username);
      if (student) user = student;
    } else if (role === 'SuperAdmin' || role === 'SchoolAdmin') {
      // For demo purposes, create a mock admin user
      user = {
        id: 'admin-1',
        username: username,
        firstName: 'Admin',
        lastName: 'User',
        email: 'admin@example.com',
        role: role,
        avatar: 'https://randomuser.me/api/portraits/men/1.jpg'
      };
    }

    if (user) {
      currentUser = user;
      
      // Log this action to audit logs
      dataService.createAuditLog({
        userId: user.id,
        userName: `${user.firstName} ${user.lastName}`,
        userRole: user.role,
        action: 'LOGIN',
        resource: 'User',
        resourceId: user.id,
        details: `User logged in: ${user.username}`,
        ipAddress: '127.0.0.1'
      });
    }

    return user;
  },

  // Logout the current user
  async logout(): Promise<boolean> {
    if (currentUser) {
      // Log this action to audit logs
      await dataService.createAuditLog({
        userId: currentUser.id,
        userName: `${currentUser.firstName} ${currentUser.lastName}`,
        userRole: currentUser.role,
        action: 'LOGOUT',
        resource: 'User',
        resourceId: currentUser.id,
        details: `User logged out: ${currentUser.username}`,
        ipAddress: '127.0.0.1'
      });
      
      currentUser = null;
      return true;
    }
    return false;
  },

  // Update user profile
  async updateUserProfile(userData: Partial<User>): Promise<User | null> {
    if (!currentUser) return null;
    
    // In a real app, this would send an API request to update the user
    currentUser = {
      ...currentUser,
      ...userData,
    };
    
    // Update the user in the appropriate mock data array
    if (currentUser.role === 'Teacher') {
      const index = mockTeachers.findIndex(t => t.id === currentUser?.id);
      if (index !== -1) {
        mockTeachers[index] = {
          ...mockTeachers[index],
          ...userData,
        } as any; // Type assertion needed due to extending User
      }
    } else if (currentUser.role === 'Student') {
      const index = mockStudents.findIndex(s => s.id === currentUser?.id);
      if (index !== -1) {
        mockStudents[index] = {
          ...mockStudents[index],
          ...userData,
        } as any; // Type assertion needed due to extending User
      }
    }
    
    // Log this action to audit logs
    await dataService.createAuditLog({
      userId: currentUser.id,
      userName: `${currentUser.firstName} ${currentUser.lastName}`,
      userRole: currentUser.role,
      action: 'UPDATE',
      resource: 'User',
      resourceId: currentUser.id,
      details: `User profile updated: ${currentUser.username}`,
      ipAddress: '127.0.0.1'
    });
    
    return currentUser;
  },

  // Change user password
  async changePassword(currentPassword: string, newPassword: string): Promise<boolean> {
    // In a real app, this would validate the current password and update it
    // For mock purposes, we'll just return success
    if (currentUser) {
      // Log this action to audit logs
      await dataService.createAuditLog({
        userId: currentUser.id,
        userName: `${currentUser.firstName} ${currentUser.lastName}`,
        userRole: currentUser.role,
        action: 'UPDATE',
        resource: 'User',
        resourceId: currentUser.id,
        details: `User password changed`,
        ipAddress: '127.0.0.1'
      });
      
      return true;
    }
    return false;
  },

  // Update user settings
  async updateUserSettings(settings: Record<string, any>): Promise<boolean> {
    // In a real app, this would update user-specific settings
    // For mock purposes, we'll just return success
    if (currentUser) {
      // Log this action to audit logs
      await dataService.createAuditLog({
        userId: currentUser.id,
        userName: `${currentUser.firstName} ${currentUser.lastName}`,
        userRole: currentUser.role,
        action: 'UPDATE',
        resource: 'UserSettings',
        resourceId: currentUser.id,
        details: `User settings updated`,
        ipAddress: '127.0.0.1'
      });
      
      return true;
    }
    return false;
  }
};

// Initialize the auth service
authService.initialize();
